import React, { CSSProperties } from 'react';
import clsx from 'clsx';
import {
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Box,
  ListItemButton,
  styled,
} from '@mui/material';
import Work from '@mui/icons-material/Work';
import People from '@mui/icons-material/People';
import ExitToApp from '@mui/icons-material/ExitToApp';
import Dashboard from '@mui/icons-material/Dashboard';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useAuth0 } from '@auth0/auth0-react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, shallowEqual } from 'react-redux';
import { selectMenuSiteTitle, selectMenuTabs } from './slice/selectors';
import Tab from 'app/components/Tabs';
import { ContractIcon } from 'app/components/Icons/Subscriptionicons';
import {
  UrlToCompany,
  UrlToPortfolio,
  UrlToSubscription,
  UrlToUser,
} from 'app/constants';
import AddCircle from '@mui/icons-material/AddCircle';
import fonts from 'app/assets/styles/fonts';
import { Link } from '../Link';
import MenuIcon from '@mui/icons-material/Menu';
import { makeThemeStyle } from 'styles/makeThemeStyle';
import Business from '@mui/icons-material/Business';
import IconText from '../IconText';

const StyledListItem = styled(ListItemButton)(({ theme }) => ({
  '& svg': { color: 'white' },
  '&:hover': {
    color: theme.palette.primary.main,
    background: theme.palette.primary.light,
    '& svg': { color: theme.palette.primary.main },
  },
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    background: theme.palette.primary.light,
    '& svg': { color: theme.palette.primary.main },
    '&:hover': {
      color: theme.palette.primary.main,
      background: theme.palette.primary.light,
    },
  },
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  margin: theme.spacing(0, 2),
}));

const drawerWidth = 240;
const useStyles = makeThemeStyle(theme => ({
  root: {
    display: 'flex',
  },
  title: {
    color: theme.palette.common.white,
  },
  appBar: {
    marginLeft: theme.spacing(7),
    width: `calc(100% - ${theme.spacing(7)})`,
    zIndex: theme.zIndex.drawer - 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    color: theme.palette.asseticPrimary.main,
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 36,
  },
  hide: {
    display: 'none',
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  paperRoot: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    '& svg': { color: theme.palette.common.white },
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: theme.spacing(7),
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(7),
    },
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...(theme.mixins.toolbar as CSSProperties),
  },
  mainList: {
    flexGrow: 1,
  },
  subIcon: {
    height: theme.spacing(3),
  },
}));

export const Menu: React.FC = () => {
  const { logout } = useAuth0();
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const { siteTitle } = useSelector(selectMenuSiteTitle, shallowEqual);
  const { tabs } = useSelector(selectMenuTabs, shallowEqual);
  const location = useLocation();

  const history = useHistory();

  const handleDrawerClick = () => {
    setOpen(!open);
  };

  const redirectToAllUsers = () => {
    history.push(UrlToUser().all_users);
  };

  const redirectToAllSubs = () => {
    history.push(UrlToSubscription().all_subs);
  };

  const redirectToAllPfs = () => {
    history.push(UrlToPortfolio().all_pfs);
  };

  const redirectToAllCompanies = () => {
    history.push(UrlToCompany().all_companies);
  };

  const redirectToCreateSub = () => {
    history.push(UrlToSubscription().create);
  };

  const handleLogout = () => {
    logout({ returnTo: window.location.origin });
  };
  return (
    <>
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: open,
        })}
        color="inherit"
      >
        <Toolbar>
          <Box
            display="flex"
            justifyContent="space-between"
            height="64px"
            width="100%"
          >
            <Box alignSelf="center">
              <Typography variant="h6" noWrap>
                {siteTitle}
              </Typography>
            </Box>
            <Box>
              <Tab tabs={tabs} />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        })}
        classes={{
          paper: clsx([
            classes.paperRoot,
            {
              [classes.drawerOpen]: open,
              [classes.drawerClose]: !open,
            },
          ]),
        }}
      >
        <Box display="flex" justifyContent="space-between">
          {open && (
            <Box
              fontWeight={500}
              marginY="auto"
              marginLeft={2}
              fontSize={fonts.xlarge}
            >
              <Link
                to={'/'}
                underline={false}
                overrideLinkStyle={classes.title}
              >
                Predictor
              </Link>
            </Box>
          )}
          <div className={classes.toolbar}>
            <IconButton
              onClick={handleDrawerClick}
              aria-label="drawer switch"
              size="large"
            >
              {open ? (
                <ChevronLeftIcon />
              ) : (
                <Box pl={0.5}>
                  <MenuIcon />
                </Box>
              )}
            </IconButton>
          </div>
        </Box>
        <StyledDivider />
        <List classes={{ root: classes.mainList }}>
          <StyledListItem
            aria-label="overview"
            onClick={() => history.push('/overview')}
            selected={
              location.pathname === '/' ||
              location.pathname.includes('/overview')
            }
          >
            <ListItemIcon>
              <Dashboard />
            </ListItemIcon>
            <ListItemText primary={'Overview'} />
          </StyledListItem>
          <StyledListItem
            aria-label="users"
            onClick={redirectToAllUsers}
            selected={location.pathname.includes('/users')}
          >
            <ListItemIcon>
              <People />
            </ListItemIcon>
            <ListItemText primary={'Users'} />
          </StyledListItem>

          <StyledListItem
            aria-label="subscriptions"
            onClick={redirectToAllSubs}
            selected={location.pathname.includes('/subscriptions')}
          >
            <ListItemIcon>
              <ContractIcon className={classes.subIcon} fill={'currentColor'} />
            </ListItemIcon>
            <ListItemText primary={'Subscriptions'} />
          </StyledListItem>
          <StyledListItem
            selected={location.pathname.includes('/portfolios')}
            aria-label="portfolios"
          >
            <ListItemIcon>
              <Work />
            </ListItemIcon>
            <ListItemText primary={'Portfolios'} onClick={redirectToAllPfs} />
          </StyledListItem>
          <StyledListItem
            selected={location.pathname.includes('/companies')}
            aria-label="companies"
            onClick={redirectToAllCompanies}
          >
            <ListItemIcon>
              <IconText icon={<Business />} text={''} />
            </ListItemIcon>
            <ListItemText primary={'Companies'} />
          </StyledListItem>
          <StyledListItem
            aria-label="create_subscription"
            onClick={redirectToCreateSub}
            selected={location.pathname.endsWith('/create-subscription')}
          >
            <ListItemIcon>
              <AddCircle />
            </ListItemIcon>
            <ListItemText primary={'Create Subscription'} />
          </StyledListItem>
        </List>
        <StyledDivider />
        <List>
          <StyledListItem onClick={handleLogout}>
            <ListItemIcon>
              <ExitToApp />
            </ListItemIcon>
            <ListItemText primary={'Logout'} />
          </StyledListItem>
        </List>
      </Drawer>
    </>
  );
};
